import React, { useState } from 'react';
import { X, Mail, Lock, Check, ArrowRight } from 'lucide-react';
import { supabase } from '../services/supabaseClient.ts';
import { handlePostLoginRedirect } from '../services/authRedirect.ts';
import { validatePassword, checkPasswordMatch, getPasswordStrength } from '../utils/validatePassword.ts';
import { COPY } from '../constants/copy.ts';
import { withRetry, withTimeout } from '../utils/retryUtils.ts';
import PostHogService from '../services/postHogService.ts';

interface SignupModalProps {
  isOpen: boolean;
  onClose: () => void;
  source?: string; // Track where signup originated from
}

export default function SignupModal({ isOpen, onClose, source = 'modal' }: SignupModalProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [success, setSuccess] = useState(false);

  const isEmailValid = email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);
  const passwordError = validatePassword(password);
  const passwordMatchError = checkPasswordMatch(password, confirmPassword);
  const passwordStrength = getPasswordStrength(password);
  const isFormValid = isEmailValid && !passwordError && !passwordMatchError && password.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setEmailError('');
    
    if (!isEmailValid) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    
    const complexityError = validatePassword(password);
    if (complexityError) {
      setError(complexityError);
      return;
    }
    
    setLoading(true);
    
    try {
      console.log('[SignupModal] Attempting signup for:', email, 'from source:', source);
      
      // Track signup attempt
      PostHogService.capture('signup_attempt', {
        source,
        email_domain: email.split('@')[1],
      });
      
      const { data, error: supaError } = await supabase.auth.signUp({ 
        email, 
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        }
      });
      
      if (supaError) {
        console.error('[SignupModal] Signup failed:', supaError.message);
        
        // Track signup failure
        PostHogService.capture('signup_failed', {
          source,
          error: supaError.message,
          email_domain: email.split('@')[1],
        });
        
        // Handle specific error cases
        if (supaError.message.includes('User already registered') || 
            supaError.message.includes('already registered') ||
            supaError.message.includes('already been registered')) {
          setError("You're already registered! Please log in instead.");
        } else {
          setError(supaError.message);
        }
        setLoading(false);
        return;
      }
      
      console.log('[SignupModal] Signup success:', data);
      
      // Track successful signup
      PostHogService.capture('signup_success', {
        source,
        email_domain: email.split('@')[1],
        requires_confirmation: !data.session,
      });
      
      // Check if email confirmation is required
      if (data.user && !data.session) {
        setSuccess(true);
        console.log('[SignupModal] Email confirmation required');
      } else if (data.session) {
        console.log('[SignupModal] Immediate login successful, redirecting...');
        
        // Track successful signup with immediate login
        PostHogService.capture('signup_immediate_login', {
          source,
          email_domain: email.split('@')[1],
        });
        
        onClose();
        await handlePostLoginRedirect();
      } else {
        setSuccess(true);
        console.log('[SignupModal] Signup completed, awaiting confirmation');
      }
      
    } catch (err: any) {
      console.error('[SignupModal] Signup error:', err);
      setError(err.message || 'An unexpected error occurred during signup.');
      
      // Track unexpected signup error
      PostHogService.capture('signup_error', {
        source,
        error: err.message || 'Unknown error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Track modal close
    PostHogService.capture('signup_modal_closed', {
      source,
      had_input: !!(email || password || confirmPassword),
    });
    
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setError('');
    setSuccess(false);
    setLoading(false);
    onClose();
  };

  const getStrengthColor = (strength: 'weak' | 'medium' | 'strong') => {
    switch (strength) {
      case 'weak': return 'text-red-500';
      case 'medium': return 'text-yellow-500';
      case 'strong': return 'text-green-500';
      default: return 'text-gray-500';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div 
        data-cy="signup-modal"
        className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Lock className="w-5 h-5" />
            {COPY.SIGNUP_MODAL_TITLE}
          </h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors group"
            aria-label="Close signup modal"
            data-cy="close-signup-modal"
          >
            <X className="w-5 h-5 text-gray-500 group-hover:text-gray-700" />
          </button>
        </div>

        {success ? (
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Check Your Email</h3>
            <p className="text-gray-600 mb-4">
              We've sent you a confirmation link. Please check your email and follow the instructions to complete your signup.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Didn't receive the email? Check your spam folder or try again in a few minutes.
            </p>
            <button
              onClick={handleClose}
              className="w-full py-2 px-4 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 transition-colors"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            {/* Benefits List */}
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <h3 className="text-sm font-semibold text-blue-900 mb-2">Get started with:</h3>
              <ul className="space-y-1">
                {COPY.SIGNUP_MODAL_BENEFITS.map((benefit, index) => (
                  <li key={index} className="flex items-center gap-2 text-sm text-blue-800">
                    <Check className="w-4 h-4 text-blue-600" />
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="signup-email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email address
                </label>
                <input
                  id="signup-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  data-cy="email-input"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                />
                {emailError && (
                  <div data-cy="email-error" className="mt-1 text-xs text-red-500">
                    {emailError}
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="signup-password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  id="signup-password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  data-cy="password-input"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white"
                  placeholder="Create a secure password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                />
                {password && (
                  <div className="mt-1 text-xs">
                    <span 
                      data-cy="password-strength"
                      className={`font-medium ${getStrengthColor(passwordStrength)}`}
                    >
                      Password strength: {passwordStrength}
                    </span>
                  </div>
                )}
                {passwordError && (
                  <div data-cy="password-error" className="mt-1 text-xs text-red-500">
                    {passwordError}
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="signup-confirm-password" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password
                </label>
                <input
                  id="signup-confirm-password"
                  name="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white"
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading}
                />
                {passwordMatchError && confirmPassword && (
                  <div className="mt-1 text-xs text-red-500">
                    {passwordMatchError}
                  </div>
                )}
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-red-600 text-sm" role="alert">{error}</p>
                </div>
              )}

              <button
                type="submit"
                data-cy="signup-submit"
                className="w-full py-3 px-4 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                disabled={loading || !isFormValid}
              >
                {loading ? (
                  'Creating account...'
                ) : (
                  <>
                    Create Free Account
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

            <div className="mt-4 text-center text-sm text-gray-500">
              Already have an account?{' '}
              <button
                onClick={() => {
                  handleClose();
                  window.location.href = '/login';
                }}
                className="text-blue-600 hover:underline"
              >
                Log in
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
} 